import { useCallback, useEffect, useRef, useState } from "react"

// 定义加载状态
type LoadStatus = "idle" | "loading" | "loaded" | "error"

// 定义状态和URL的记录类型，键为字符串
type StatusRecord = Record<string, LoadStatus>
type UrlRecord = Record<string, string[]>

// Worker 脚本内容（内联，纯 JavaScript）
const workerScript = `
// 存储当前正在进行的任务及其对应的 AbortController
const activeTasks = new Map();

/**
 * 使用 fetch 预加载一组图片 URL。
 * 这个过程会填充浏览器的 HTTP 缓存，但不传递 Blob 数据。
 */
async function preload(urls, taskId) {
  for (const url of urls) {
      try {
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
            throw new Error(\`HTTP error! status: \${response.status}\`);
        }
        // 只读取响应，填充缓存，不传递 Blob
        await response.blob();

      } catch (e) {
         self.postMessage({
            type: 'image_error',
            taskId,
            originalUrl: url,
            error: e.message
        });
      }
  }
}

// 监听来自主线程的消息
self.onmessage = async (event) => {
  const { type, taskId, urls } = event.data;

  if (type === 'preload') {
    try {
      await preload(urls, taskId);
      // 所有图片处理完毕后，发送一条完成消息，并附上原始URL列表
      self.postMessage({ type: 'complete', taskId, originalUrls: urls });
    } catch (error) {
      // 向主线程发送错误消息
      self.postMessage({
        type: 'error',
        taskId,
        error: error instanceof Error ? error.message : "发生未知错误",
      });
    }
  } else if (type === 'cancel') {
    const controller = activeTasks.get(taskId);
    if (controller) {
      controller.abort();
      activeTasks.delete(taskId);
    }
  }
};
`

export const useImagePreloader = () => {
	const workerRef = useRef<Worker | null>(null)
	const [loadedUrls, setLoadedUrls] = useState<UrlRecord>({})
	const [statuses, setStatuses] = useState<StatusRecord>({})
	// 不再需要 ObjectURL 映射，直接使用原始 URL

	// 第一张图片快速加载函数
	const loadFirstImage = useCallback((url: string): Promise<string> => {
		return new Promise((resolve, reject) => {
			const img = new Image()
			img.onload = () => resolve(url)
			img.onerror = reject
			img.src = url
		})
	}, [])

	// 初始化和清理 Web Worker
	useEffect(() => {
		// 创建 Blob URL 来避免跨域问题
		const blob = new Blob([workerScript], { type: "application/javascript" })
		const workerUrl = URL.createObjectURL(blob)
		// 使用 window.Worker 来绕过 Next.js/Turbopack 的静态分析
		const worker = new window.Worker(workerUrl)
		workerRef.current = worker

		const handleMessage = (event: MessageEvent) => {
			const { type, taskId: key, originalUrls, error } = event.data

			if (!key) return

			if (type === "complete") {
				// 所有图片加载完成时才更新状态
				setStatuses((prev) => ({ ...prev, [key]: "loaded" }))
				setLoadedUrls((prev) => {
					const currentUrls = prev[key] || []
					return { ...prev, [key]: [...currentUrls, ...(originalUrls || [])] }
				})
			} else if (type === "error") {
				console.error(`Worker: Preloading error for task ${key}:`, error)
				setStatuses((prev) => ({ ...prev, [key]: "error" }))
			}
		}

		worker.addEventListener("message", handleMessage)

		return () => {
			worker.terminate()
			URL.revokeObjectURL(workerUrl) // 清理 worker 的 Blob URL
		}
	}, [])

	// 混合预加载：超过5张图片时，第一张图片主线程，其他图片 Worker
	const preload = useCallback(
		async (key: string, urls: string[]) => {
			if (!urls || urls.length === 0) return

			if (!statuses[key] || statuses[key] === "idle") {
				setStatuses((prev) => ({ ...prev, [key]: "loading" }))

				// 只有超过5张图片时才采用混合加载方案
				if (urls.length > 5) {
					// 第一张图片在主线程快速加载，立即更新状态用于封面显示
					if (urls.length > 0) {
						try {
							const firstUrl = await loadFirstImage(urls[0])
							setLoadedUrls((prev) => ({ ...prev, [key]: [firstUrl] }))
						} catch (error) {
							console.error(`Failed to load first image:`, error)
						}
					}

					// 其他图片在 Worker 中预加载
					if (urls.length > 1 && workerRef.current) {
						workerRef.current.postMessage({
							type: "preload",
							taskId: key,
							urls: urls.slice(1), // 跳过第一张图片
						})
					}
				} else {
					// 5张及以下图片，全部在 Worker 中预加载
					if (workerRef.current) {
						workerRef.current.postMessage({
							type: "preload",
							taskId: key,
							urls: urls,
						})
					}
				}
			}
		},
		[statuses, loadFirstImage],
	)

	return {
		preload,
		statuses,
		loadedUrls,
	}
}
